import React from 'react';
import clsx from 'clsx';
import {
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigation,
  useRevalidator,
  useSearchParams
} from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import type { ActionResult } from '@oysterjs/core/graphql/actions';
import { Await } from '@oysterjs/ui/async';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { PageTitle } from '@oysterjs/ui/page';
import { SegmentedControl, SegmentedControlOption } from '@oysterjs/ui/segmented-control';
import { Select } from '@oysterjs/ui/select';
import { Spinner } from '@oysterjs/ui/spinner';
import { Textarea } from '@oysterjs/ui/textarea';

import type { SmartFillAnswersSubmission } from '~/api/data/smart-fill/mutations';
import {
  openSmartFillSidePanel,
  type SmartFillPanelOutcome
} from '~/components/cara-input/smart-fill-extension';
import {
  SmartFillAnswerSource,
  type AnswerSmartFillFormBucketMutation,
  type SmartFillFormAnswerInput,
  type SmartFillFormBucketQuestionsFieldsFragment,
  type SmartFillCarrierSiteFieldsFragment,
  type SmartFillFormQuestionFieldsFragment
} from '~/api/graphql';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { FloatingPendingChangesBar } from '~/components/settings/floating-pending-changes-bar';
import {
  PendingChangesSaveToast,
  type PendingChangesSaveMessages
} from '~/components/settings/pending-changes-save';
import {
  CoverageSummary,
  QUESTION_STATUS,
  QuestionMap
} from '~/components/smart-fill/form-bucket-coverage';
import {
  type AnswerControl,
  type AnswerDrafts,
  type QuestionSection,
  type QuestionStatus,
  answerControl,
  answerKey,
  bucketSources,
  bucketTitle,
  changedAnswers,
  controlChoices,
  controlValue,
  coverageCounts,
  isRecordQuestion,
  questionSections,
  questionStatus,
  saveOutcome,
  carrierSiteName,
  siteGroups,
  unsavedDrafts
} from '~/components/smart-fill/form-buckets';
import { formatRelativeTime } from '~/components/table-formatters';
import { pluralize } from '~/lib/pluralize';

type BucketQuestions = SmartFillFormBucketQuestionsFieldsFragment;
type CarrierSite = SmartFillCarrierSiteFieldsFragment;
type Question = SmartFillFormQuestionFieldsFragment;
type Answer = Question['answers'][number];

type SmartFillBucketLoaderData = {
  carrierSites?: CarrierSite[] | null;
  formBucketQuestions?: BucketQuestions | null;
};

type View = 'needed' | 'all';

// The server refuses longer answers.
const MAX_ANSWER_LENGTH = 1000;

const CARD_CLASS =
  'rounded-xl bg-white shadow ring-1 ring-neutral-950/5 dark:bg-neutral-800 dark:ring-white/10';

const questionAnchor = (index: number) => `smart-fill-question-${index + 1}`;

/** Picks the carrier site to configure the bucket for; the empty value is its line in general. */
const SitePicker = ({
  carrierSites,
  lineOfBusiness,
  value,
  disabled,
  onChange
}: {
  carrierSites: CarrierSite[];
  lineOfBusiness: string;
  value: string;
  disabled: boolean;
  onChange: (carrierSiteId: string) => void;
}) => {
  const options = (sites: CarrierSite[]) =>
    sites.map((site) => (
      <option key={site.id} value={site.id}>
        {carrierSiteName(site)}
      </option>
    ));
  const { forLine, others } = siteGroups(carrierSites, lineOfBusiness);

  return (
    <Field className="w-full sm:max-w-sm">
      <Label>Configure for a carrier site</Label>
      <Select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        <option value="">None: general questions</option>
        {forLine.length > 0 && (
          <optgroup label={`${lineOfBusiness} sites`}>{options(forLine)}</optgroup>
        )}
        {others.length > 0 && (
          <optgroup label={forLine.length > 0 ? 'Other sites' : 'Carrier sites'}>
            {options(others)}
          </optgroup>
        )}
      </Select>
    </Field>
  );
};

/** The next step after asking Smart Fill to open: choose the bucket, then Paste on host when there is one. */
const smartFillHint = (
  outcome: SmartFillPanelOutcome,
  bucketName: string,
  host: string | null
): string => {
  const bucket = `“${bucketName}”`;
  switch (outcome) {
    case 'missing':
      return host
        ? `Install Cara Smart Fill, then use it with this bucket on ${host}.`
        : `Install Cara Smart Fill, then choose ${bucket} in it.`;
    case 'opened':
      return host
        ? `In Smart Fill, choose ${bucket}, then Paste on ${host}.`
        : `In Smart Fill, choose ${bucket}.`;
    case 'failed':
      return host
        ? `Open Cara Smart Fill from Chrome's toolbar, choose ${bucket}, then Paste on ${host}.`
        : `Open Cara Smart Fill from Chrome's toolbar, then choose ${bucket}.`;
  }
};

const SiteDetails = ({
  site,
  switching,
  opening,
  onFill
}: {
  site: CarrierSite | null;
  switching: boolean;
  opening: boolean;
  onFill: () => void;
}) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
    {switching && (
      <span className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400">
        <Spinner size="size-4" color="neutral" />
        Loading questions...
      </span>
    )}
    {site && (
      <a
        href={`https://${site.host}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-medium text-primary-600 hover:text-primary-800 hover:underline dark:text-primary-400 dark:hover:text-primary-300"
      >
        {site.host}
        <ArrowTopRightOnSquareIcon className="size-4" />
      </a>
    )}
    {site && !switching && (
      <Button color="sky" loading={opening} onClick={onFill}>
        <ClipboardDocumentCheckIcon data-slot="icon" />
        Fill on {site.carrier}
      </Button>
    )}
  </div>
);

const AnswerInput = ({
  control,
  label,
  value,
  onChange
}: {
  control: AnswerControl;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const change = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => onChange(event.target.value);

  switch (control.type) {
    case 'yes-no':
    case 'select':
      return (
        <Select aria-label={label} value={value} onChange={change}>
          <option value="">No answer</option>
          {controlChoices(control, value).map((choice) => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </Select>
      );
    case 'textarea':
      return (
        <Textarea
          aria-label={label}
          rows={3}
          maxLength={MAX_ANSWER_LENGTH}
          value={value}
          onChange={change}
        />
      );
    case 'number':
      return (
        <Input
          aria-label={label}
          type="number"
          inputMode="decimal"
          step="any"
          autoComplete="off"
          value={value}
          onChange={change}
        />
      );
    default:
      return (
        <Input
          aria-label={label}
          type={control.type}
          autoComplete="off"
          maxLength={MAX_ANSWER_LENGTH}
          value={value}
          onChange={change}
        />
      );
  }
};

const AnswerRow = ({
  question,
  answer,
  control,
  bucketLabel,
  draft,
  onDraft
}: {
  question: Question;
  answer: Answer;
  control: AnswerControl;
  bucketLabel: string;
  draft: string | undefined;
  onDraft: (key: string, value: string) => void;
}) => {
  const record = isRecordQuestion(question)
    ? `${answer.recordLabel}${answer.recordExists ? '' : ' (new)'}`
    : null;
  const stored = controlValue(control, answer.value);

  if (answer.source === SmartFillAnswerSource.Bucket) {
    return (
      <div>
        {record && (
          <p className="mb-1 text-xs font-medium text-neutral-500 dark:text-neutral-400">
            {record}
          </p>
        )}
        <div className="flex items-start justify-between gap-3 rounded-lg bg-neutral-50 px-3 py-1.5 dark:bg-neutral-900">
          <p className="min-w-0 break-words text-sm/6 text-neutral-900 dark:text-white">
            {control.type === 'yes-no' ? stored : answer.value}
          </p>
          <Badge color="sky" className="mt-0.5 shrink-0">
            {bucketLabel}
          </Badge>
        </div>
      </div>
    );
  }

  const value = draft ?? stored;
  const unsaved = value.trim() !== stored;
  return (
    <div>
      {record && (
        <p className="mb-1 text-xs font-medium text-neutral-500 dark:text-neutral-400">{record}</p>
      )}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <AnswerInput
            control={control}
            label={record ? `${question.question} (${record})` : question.question}
            value={value}
            onChange={(next) => onDraft(answerKey(question, answer.recordIndex), next)}
          />
        </div>
        {unsaved ? (
          <Badge color="amber" className="mt-1.5 shrink-0">
            Unsaved
          </Badge>
        ) : (
          answer.source === SmartFillAnswerSource.Agent && (
            <Badge color="emerald" className="mt-1.5 shrink-0">
              Your answer
            </Badge>
          )
        )}
      </div>
    </div>
  );
};

const QuestionRow = ({
  question,
  index,
  status,
  highlighted,
  bucketLabel,
  drafts,
  onDraft
}: {
  question: Question;
  index: number;
  status: QuestionStatus;
  highlighted: boolean;
  bucketLabel: string;
  drafts: AnswerDrafts;
  onDraft: (key: string, value: string) => void;
}) => {
  const control = answerControl(question);
  return (
    <div
      id={questionAnchor(index)}
      className={clsx(
        'scroll-mt-24 px-4 py-4 transition-colors duration-500 sm:px-6',
        highlighted && 'bg-sky-50 dark:bg-sky-500/10'
      )}
    >
      <div className="grid gap-x-8 gap-y-3 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="flex min-w-0 gap-3">
          <span
            aria-hidden="true"
            className={clsx('mt-1.5 size-2.5 shrink-0 rounded-sm', QUESTION_STATUS[status].swatch)}
          />
          <div className="min-w-0">
            <p className="break-words text-sm/6 font-medium text-neutral-900 dark:text-white">
              {question.question}
            </p>
            {question.label && question.label !== question.question && (
              <p className="text-xs/5 text-neutral-500 dark:text-neutral-400">{question.label}</p>
            )}
          </div>
        </div>
        <div className="min-w-0 space-y-3">
          {question.answers.map((answer) => (
            <AnswerRow
              key={answer.recordIndex}
              question={question}
              answer={answer}
              control={control}
              bucketLabel={bucketLabel}
              draft={drafts[answerKey(question, answer.recordIndex)]}
              onDraft={onDraft}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const QuestionSectionCard = ({
  section,
  statuses,
  focusedIndex,
  bucketLabel,
  drafts,
  onDraft
}: {
  section: QuestionSection;
  statuses: QuestionStatus[];
  focusedIndex: number | undefined;
  bucketLabel: string;
  drafts: AnswerDrafts;
  onDraft: (key: string, value: string) => void;
}) => (
  <section aria-label={section.name}>
    <h2 className="flex items-baseline gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
      {section.name}
      <span className="text-xs font-normal text-neutral-500 dark:text-neutral-400">
        {section.questions.length} {pluralize(section.questions.length, 'question')}
      </span>
    </h2>
    <div
      className={clsx(
        CARD_CLASS,
        'mt-3 divide-y divide-neutral-100 overflow-hidden dark:divide-neutral-700'
      )}
    >
      {section.questions.map(({ question, index }) => (
        <QuestionRow
          key={index}
          question={question}
          index={index}
          status={statuses[index]}
          highlighted={focusedIndex === index}
          bucketLabel={bucketLabel}
          drafts={drafts}
          onDraft={onDraft}
        />
      ))}
    </div>
  </section>
);

// How long a save's toast stays up, as on the settings pages.
const TOAST_MS = 5_000;

/** What the last save did, and the site it was for when there was one. */
type SaveToast = { error: string | null; host: string | null };

const saveMessages = (host: string | null): PendingChangesSaveMessages => ({
  successTitle: 'Answers saved',
  successDescription: host
    ? `Open ${host} and run Smart Fill with this bucket.`
    : 'Smart Fill uses them on every carrier site that asks.',
  errorTitle: 'Answers not saved',
  errorDescriptionPrefix: 'Your answers are still here.'
});

const SmartFillBucketDetail = ({
  loaded,
  carrierSites
}: {
  loaded: BucketQuestions;
  carrierSites: CarrierSite[];
}) => {
  const fetcher = useFetcher<ActionResult<AnswerSmartFillFormBucketMutation>>();
  const revalidator = useRevalidator();
  const navigation = useNavigation();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  // What the last save returned, which stands until the loader reads newer questions.
  const [saved, setSaved] = React.useState<{ over: BucketQuestions; questions: BucketQuestions }>();
  const current = saved?.over === loaded ? saved.questions : loaded;
  const { formBucket, site, questions } = current;

  const [drafts, setDrafts] = React.useState<AnswerDrafts>({});
  const [view, setView] = React.useState<View>('needed');
  const [toast, setToast] = React.useState<SaveToast | null>(null);
  const [fillHint, setFillHint] = React.useState<string | null>(null);
  const [opening, setOpening] = React.useState(false);
  const [focus, setFocus] = React.useState<{ index: number } | null>(null);
  const submitted = React.useRef<SmartFillFormAnswerInput[]>([]);
  const handled = React.useRef<unknown>(null);

  const statuses = React.useMemo(() => questions.map(questionStatus), [questions]);
  const counts = React.useMemo(() => coverageCounts(questions), [questions]);
  const changes = React.useMemo(() => changedAnswers(questions, drafts), [questions, drafts]);
  const sections = React.useMemo(
    () =>
      questionSections(
        questions,
        view === 'needed' ? (question) => questionStatus(question) === 'needed' : undefined
      ),
    [questions, view]
  );

  const saving = fetcher.state !== 'idle';
  const switching =
    navigation.state === 'loading' && navigation.location.pathname === location.pathname;
  const shownSite = switching
    ? (new URLSearchParams(navigation.location.search).get('site') ?? '')
    : (site?.id ?? '');
  const pickable =
    !site || carrierSites.some((option) => option.id === site.id)
      ? carrierSites
      : [site, ...carrierSites];
  const lineOfBusiness = formBucket.lineOfBusiness;
  const bucketLabel = formBucket.forms.length > 0 ? 'From form' : 'From workspace';

  React.useEffect(() => {
    if (fetcher.state !== 'idle' || !fetcher.data || handled.current === fetcher.data) {
      return;
    }
    handled.current = fetcher.data;
    const outcome = saveOutcome(fetcher.data);
    if ('failure' in outcome) {
      setToast({ error: outcome.failure.message, host: null });
      // Reading the newer bucket keeps the drafts, so saving again can succeed.
      if (outcome.failure.conflict) {
        revalidator.revalidate();
      }
      return;
    }
    setSaved({ over: loaded, questions: outcome.saved });
    setDrafts((previous) => unsavedDrafts(previous, submitted.current));
    setToast({ error: null, host: outcome.saved.site?.host ?? null });
  }, [fetcher.state, fetcher.data, loaded, revalidator]);

  React.useEffect(() => {
    if (!toast) {
      return;
    }
    const timer = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  React.useEffect(() => {
    if (!focus) {
      return;
    }
    const element = document.getElementById(questionAnchor(focus.index));
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element?.querySelector<HTMLElement>('input, select, textarea')?.focus({ preventScroll: true });
    const timer = window.setTimeout(() => setFocus(null), 2000);
    return () => window.clearTimeout(timer);
  }, [focus]);

  const dirty = changes.length > 0;
  React.useEffect(() => {
    if (!dirty) {
      return;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const updateDraft = React.useCallback((key: string, value: string) => {
    setDrafts((previous) => ({ ...previous, [key]: value }));
  }, []);

  const chooseSite = (carrierSiteId: string) => {
    setFillHint(null);
    setSearchParams(carrierSiteId ? { site: carrierSiteId } : {}, { replace: true });
  };

  // The panel opens before the carrier's tab: the extension refuses a request
  // from a tab that is no longer the active one.
  const openSmartFill = async () => {
    setOpening(true);
    const outcome = await openSmartFillSidePanel(formBucket.workspaceId);
    setOpening(false);
    setFillHint(smartFillHint(outcome, formBucket.bucket.name, site?.host ?? null));
    if (site && outcome !== 'missing') {
      window.open(`https://${site.host}`, '_blank', 'noopener,noreferrer');
    }
  };

  // A square for an answered question is out of the needs-an-answer view, so the
  // view widens rather than scrolling to nothing.
  const jumpTo = (index: number) => {
    if (view === 'needed' && statuses[index] !== 'needed') {
      setView('all');
    }
    setFocus({ index });
  };

  const save = () => {
    if (!dirty || saving) {
      return;
    }
    submitted.current = changes;
    setToast(null);
    const submission: SmartFillAnswersSubmission = {
      expectedRevision: current.revision,
      carrierSiteId: site?.id ?? null,
      answers: changes
    };
    fetcher.submit(submission, { method: 'post', encType: 'application/json' });
  };

  const builtAt = new Date(formBucket.builtAt);
  const workspaceTitle = formBucket.workspaceTitle || 'a workspace';

  return (
    <PageTitle
      eyebrow="Smart Fill"
      eyebrowHref="/app/smart-fill"
      title={bucketTitle(formBucket)}
      badge={<Badge color="sky">{lineOfBusiness}</Badge>}
      description={
        formBucket.forms.length > 0
          ? `Built ${formatRelativeTime(builtAt)} from ${bucketSources(formBucket)} in ${workspaceTitle}`
          : `Generated ${formatRelativeTime(builtAt)} from ${workspaceTitle}`
      }
      menu={
        <div className="flex flex-wrap gap-3">
          {/* A site's view fills from its own button, which also opens the site. */}
          {!site && (
            <Button color="sky" loading={opening} onClick={() => void openSmartFill()}>
              <ClipboardDocumentCheckIcon data-slot="icon" />
              Open in Smart Fill
            </Button>
          )}
          <Button outline href={`/app/workspaces/${formBucket.workspaceId}`}>
            <SparklesIcon data-slot="icon" />
            Open workspace
          </Button>
        </div>
      }
    >
      {/* Room below the last question for the floating save bar. */}
      <div className="space-y-8 pb-24">
        <section aria-label="Coverage" className={CARD_CLASS}>
          {/* The server lists no carrier sites while they are off for the account. */}
          {pickable.length > 0 && (
            <div className="flex flex-col gap-4 border-b border-neutral-100 px-4 py-4 dark:border-neutral-700 sm:flex-row sm:items-end sm:justify-between sm:px-6">
              <SitePicker
                carrierSites={pickable}
                lineOfBusiness={lineOfBusiness}
                value={shownSite}
                disabled={saving}
                onChange={chooseSite}
              />
              <SiteDetails
                site={site ?? null}
                switching={switching}
                opening={opening}
                onFill={() => void openSmartFill()}
              />
            </div>
          )}
          {fillHint && (
            <p
              role="status"
              className="border-b border-neutral-100 px-4 py-3 text-sm text-neutral-600 dark:border-neutral-700 dark:text-neutral-300 sm:px-6"
            >
              {fillHint}
            </p>
          )}
          <div
            className={clsx(
              'grid gap-8 px-4 py-6 transition-opacity sm:px-6 lg:grid-cols-[auto_minmax(0,1fr)] lg:gap-12',
              switching && 'opacity-60'
            )}
          >
            <CoverageSummary counts={counts} />
            <QuestionMap
              questions={questions}
              statuses={statuses}
              caption={
                site
                  ? `In the order ${site.carrier} asks them`
                  : `Everything ${lineOfBusiness} sites ask for`
              }
              onSelect={jumpTo}
            />
          </div>
        </section>

        <div className={clsx('space-y-8 transition-opacity', switching && 'opacity-60')}>
          <SegmentedControl
            aria-label="Questions to show"
            value={view}
            onChange={setView}
            className="max-w-md"
          >
            <SegmentedControlOption value="needed">
              Needs an answer ({counts.needed})
            </SegmentedControlOption>
            <SegmentedControlOption value="all">
              All questions ({counts.total})
            </SegmentedControlOption>
          </SegmentedControl>

          {sections.length > 0 ? (
            sections.map((section) => (
              <QuestionSectionCard
                key={section.name}
                section={section}
                statuses={statuses}
                focusedIndex={focus?.index}
                bucketLabel={bucketLabel}
                drafts={drafts}
                onDraft={updateDraft}
              />
            ))
          ) : (
            <LibraryListEmptyState
              icon={CheckCircleIcon}
              title={counts.total > 0 ? 'Nothing left to answer' : 'No questions to answer'}
              description={
                site
                  ? counts.total > 0
                    ? `Every question ${site.carrier} asks has an answer. Open ${site.host} and run Smart Fill with this bucket.`
                    : `None of the questions ${site.carrier} asks are ones a bucket can answer.`
                  : counts.total > 0
                    ? `The bucket answers everything ${lineOfBusiness} sites ask for. Configure it for a carrier site to fill one.`
                    : `Smart Fill has no ${lineOfBusiness} questions to ask yet.`
              }
            />
          )}
        </div>
      </div>

      <FloatingPendingChangesBar
        show={dirty || saving}
        saving={saving}
        saveLabel="Save answers"
        onDiscard={() => setDrafts({})}
        onSave={save}
      />
      <PendingChangesSaveToast
        saved={toast !== null}
        error={toast?.error ?? null}
        messages={saveMessages(toast?.host ?? null)}
      />
    </PageTitle>
  );
};

export const SmartFillBucketPage = () => {
  const loader = useLoaderData() as { data: Promise<SmartFillBucketLoaderData> } | undefined;

  return (
    <React.Suspense fallback={<LibraryListLoading title="Loading bucket..." />}>
      <Await resolve={loader?.data}>
        {({ carrierSites, formBucketQuestions }) =>
          formBucketQuestions ? (
            // Keyed so that answers typed for one bucket never carry over to the next.
            <SmartFillBucketDetail
              key={formBucketQuestions.formBucket.bucket.id}
              loaded={formBucketQuestions}
              carrierSites={carrierSites ?? []}
            />
          ) : (
            <PageTitle eyebrow="Smart Fill" eyebrowHref="/app/smart-fill" title="Bucket not found">
              <LibraryListEmptyState
                icon={ClipboardDocumentCheckIcon}
                title="Bucket not found"
                description="It may have been deleted, or you can't see the workspace it was built in."
              />
            </PageTitle>
          )
        }
      </Await>
    </React.Suspense>
  );
};
